import { useMemo, useState } from "react";
import { Link } from "@tanstack/react-router";
import { Search, X, ArrowRight } from "lucide-react";
import type { HelpTopic } from "@/lib/help-content";

type SearchableTopic = HelpTopic & { sectionLabel: string; sectionLink: string };

export function HelpSearch({
  topics,
  placeholder = "Search help topics…",
}: {
  topics: SearchableTopic[];
  placeholder?: string;
}) {
  const [query, setQuery] = useState("");
  const q = query.trim().toLowerCase();

  const results = useMemo(() => {
    if (!q) return [];
    return topics
      .map((t) => {
        const haystack = [t.title, t.description, t.sectionLabel, ...t.bullets]
          .join(" \n ")
          .toLowerCase();
        const matched = haystack.includes(q);
        if (!matched) return null;
        const matchedBullets = t.bullets.filter((b) => b.toLowerCase().includes(q));
        return { topic: t, matchedBullets };
      })
      .filter(Boolean) as { topic: SearchableTopic; matchedBullets: string[] }[];
  }, [q, topics]);

  return (
    <div className="space-y-4">
      <div className="relative">
        <Search
          aria-hidden="true"
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
        />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          aria-label="Search help topics"
          className="w-full rounded-2xl border border-border bg-card py-3 pl-10 pr-10 text-sm shadow-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
        />
        {query && (
          <button
            type="button"
            onClick={() => setQuery("")}
            aria-label="Clear search"
            className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {q && (
        <div
          role="region"
          aria-live="polite"
          aria-label="Search results"
          className="rounded-2xl border border-border bg-card p-4 shadow-soft"
        >
          {results.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No topics match “{query}”. Try a different keyword.
            </p>
          ) : (
            <ul className="space-y-3">
              {results.map(({ topic, matchedBullets }) => {
                const Icon = topic.icon;
                return (
                  <li key={`${topic.sectionLink}-${topic.title}`}>
                    <Link
                      to={topic.sectionLink as never}
                      className="group flex items-start gap-3 rounded-xl p-2 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <div className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-hero text-primary-foreground">
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <h4 className="text-sm font-semibold leading-tight group-hover:text-primary">
                            {topic.title}
                          </h4>
                          <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">
                            {topic.sectionLabel}
                          </span>
                        </div>
                        <p className="text-xs text-muted-foreground">{topic.description}</p>
                        {matchedBullets.length > 0 && (
                          <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
                            {matchedBullets.slice(0, 2).map((b, i) => (
                              <li key={i} className="line-clamp-1">
                                · {b}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                      <ArrowRight
                        aria-hidden="true"
                        className="mt-1 h-4 w-4 shrink-0 text-muted-foreground group-hover:text-primary"
                      />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
